/**
 * Public page for experts to respond to work requests (accept/decline/quote).
 * No login required. Accessed via link: /expert-option/:token?expertId=xxx
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const ROLE_LABELS = {
  ENGINEER_PS1: 'PS1 (Design) Engineer',
  INSTALLER_PS3: 'PS3 (Installation)',
  DESIGNER_PS4: 'PS4 (Design Review)',
  COA_ENGINEER: 'CoA Building Report Engineer',
};

function ExpertOption() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const expertId = searchParams.get('expertId');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ status: 'ACCEPTED', responseMessage: '', quotedAmount: '' });

  useEffect(() => {
    if (!token) return;
    fetch(`/api/expert-option/${token}?expertId=${expertId || ''}`)
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        setError(d.error ? d : null);
      })
      .catch((e) => setError({ error: e.message }))
      .finally(() => setLoading(false));
  }, [token, expertId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!token || !expertId) return;
    setSubmitting(true);
    fetch(`/api/expert-option/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        expertId: parseInt(expertId, 10),
        status: form.status,
        responseMessage: form.responseMessage || undefined,
        quotedAmount: form.status === 'QUOTED' ? parseFloat(form.quotedAmount) : undefined,
      }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        setSubmitted(true);
      })
      .catch((e) => {
        setError({ error: e.message });
      })
      .finally(() => setSubmitting(false));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (error?.error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg shadow p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold text-red-700 mb-2">Request Not Found</h1>
          <p className="text-gray-600">{error.error}</p>
          <p className="text-sm text-gray-500 mt-4">The link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg shadow p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold text-green-700 mb-2">Response Submitted</h1>
          <p className="text-gray-600">Thank you for your response. Cornerstone Building Service will contact you shortly.</p>
        </div>
      </div>
    );
  }

  const { request, expert, option } = data || {};
  if (!request || !expert) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg shadow p-8 max-w-md text-center">
          <p className="text-gray-600">Invalid request. Please use the link from your email.</p>
        </div>
      </div>
    );
  }

  if (option?.status !== 'PENDING') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg shadow p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Already Responded</h1>
          <p className="text-gray-600">You have already responded to this request.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-lg p-8 max-w-lg w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Work Request</h1>
        <p className="text-gray-600 mb-6">Cornerstone Building Service is requesting your services for the following project:</p>
        <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
          <p><strong>Role:</strong> {ROLE_LABELS[request.expertRole] || request.expertRole}</p>
          <p><strong>Building:</strong> {request.buildingName || 'N/A'}</p>
          <p><strong>Address:</strong> {request.address || 'N/A'}</p>
          <p><strong>SS Codes:</strong> {request.ssCodes || 'N/A'}</p>
        </div>
        <p className="text-sm text-gray-500 mb-4">Responding as: {expert.name} ({expert.email})</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your response</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input type="radio" name="status" value="ACCEPTED" checked={form.status === 'ACCEPTED'} onChange={() => setForm((p) => ({ ...p, status: 'ACCEPTED' }))} />
                <span>I accept</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="status" value="QUOTED" checked={form.status === 'QUOTED'} onChange={() => setForm((p) => ({ ...p, status: 'QUOTED' }))} />
                <span>I can provide a quote</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="status" value="DECLINED" checked={form.status === 'DECLINED'} onChange={() => setForm((p) => ({ ...p, status: 'DECLINED' }))} />
                <span>I decline</span>
              </label>
            </div>
          </div>
          {form.status === 'QUOTED' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.quotedAmount}
                onChange={(e) => setForm((p) => ({ ...p, quotedAmount: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required={form.status === 'QUOTED'}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
            <textarea
              value={form.responseMessage}
              onChange={(e) => setForm((p) => ({ ...p, responseMessage: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              placeholder="Any additional notes..."
            />
          </div>
          <button
            type="submit"
            disabled={submitting || (form.status === 'QUOTED' && !form.quotedAmount)}
            className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium"
          >
            {submitting ? 'Submitting...' : 'Submit Response'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ExpertOption;
